import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, TextField, MenuItem, IconButton, Tooltip
} from '@mui/material';
import { Clear, ExpandMore } from '@mui/icons-material';
import './CSS/AIModels.css';
import { API_URL } from '../apiConfig';

const AIModels = () => {
  const [state, setState] = useState({
    models: [],
    loading: true,
    error: '',
    filters: { type: '', name: '', tags: '' },
    expandedRow: null
  });

  useEffect(() => {
    const fetchModels = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setState(prev => ({ ...prev, error: 'No access token found', loading: false }));
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/models`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setState(prev => ({ ...prev, models: response.data, loading: false }));
      } catch (err) {
        setState(prev => ({
          ...prev,
          error: err.response?.status === 403
            ? 'You need to verify your email to access this page. Go to the settings page to resolve this.'
            : 'Failed to fetch models',
          loading: false
        }));
      }
    };
    fetchModels();
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setState(prev => ({ ...prev, filters: { ...prev.filters, [name]: value } }));
  };

  const clearFilters = () => {
    setState(prev => ({ ...prev, filters: { type: '', name: '', tags: '' } }));
  };

  const toggleRowExpansion = (index) => {
    setState(prev => ({ ...prev, expandedRow: prev.expandedRow === index ? null : index }));
  };

  const filteredModels = state.models.filter(model => 
    (!state.filters.type || model.model_type.toLowerCase() === state.filters.type.toLowerCase()) &&
    (!state.filters.name || model.name.toLowerCase().includes(state.filters.name.toLowerCase())) &&
    (!state.filters.tags || (model.tags && model.tags.toLowerCase().includes(state.filters.tags.toLowerCase())))
  );

  if (state.loading) {
    return (
      <div className="flex h-screen justify-center items-center">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-6 py-1">
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-4 bg-gray-300 rounded col-span-2"></div>
                <div className="h-4 bg-gray-300 rounded col-span-1"></div>
              </div>
              <div className="h-4 bg-gray-300 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="flex h-screen justify-center items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="py-4">
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
              <p className="font-bold">Error</p>
              <p>{state.error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-6 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <Typography variant="h4" className="text-3xl font-semibold text-gray-800 mb-6">
          AI Models
        </Typography>

        <Paper className="mb-6 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {['type', 'name', 'tags'].map(field => (
              <TextField
                key={field}
                select={field === 'type'}
                name={field}
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                value={state.filters[field]}
                onChange={handleFilterChange}
                className={field === 'type' ? 'w-40' : 'w-64'}
              >
                {field === 'type' && [
                  <MenuItem key="all" value="">All</MenuItem>,
                  <MenuItem key="video" value="video">Video</MenuItem>,
                  <MenuItem key="audio" value="audio">Audio</MenuItem>,
                  <MenuItem key="image" value="image">Image</MenuItem>
                ]}
              </TextField>
            ))}
            <Tooltip title="Clear Filters">
              <IconButton onClick={clearFilters} size="small">
                <Clear />
              </IconButton>
            </Tooltip>
          </div>
        </Paper>

        <TableContainer component={Paper} className="shadow-md">
          <Table>
            <TableHead className="bg-gray-100">
              <TableRow>
                {['Name', 'Type', 'Details', 'Input Format', 'Trained On', 'Created At', 'Updated At'].map(header => (
                  <TableCell key={header}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModels.map((model, index) => (
                <React.Fragment key={model.model_uuid}>
                  <TableRow className="hover:bg-gray-50">
                    <TableCell>{model.name}</TableCell>
                    <TableCell>{model.model_type}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => toggleRowExpansion(index)} size="small">
                        <ExpandMore />
                      </IconButton>
                    </TableCell>
                    <TableCell>{model.input_format || 'N/A'}</TableCell>
                    <TableCell>{model.name_A && model.name_B ? `${model.name_A} & ${model.name_B}` : model.name_A || model.name_B || 'N/A'}</TableCell>
                    <TableCell>{new Date(model.created_at).toLocaleString()}</TableCell>
                    <TableCell>{new Date(model.updated_at).toLocaleString()}</TableCell>
                  </TableRow>
                  {state.expandedRow === index && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-gray-50">
                        <div className="p-4">
                          <Typography variant="h6" className="mb-2">Additional Information</Typography>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                              { label: 'Description', value: model.description },
                              { label: 'Resolution', value: model.resolution },
                              { label: 'Sample Rate', value: model.sample_rate },
                              { label: 'Duration', value: model.duration_in_minutes ? `${model.duration_in_minutes} minutes` : null },
                              { label: 'File Type', value: model.file_type },
                              { label: 'Source', value: model.source },
                              { label: 'Background Info', value: model.background_info },
                              { label: 'Additional Info', value: model.additional_info }
                            ].map(({ label, value }) => (
                              <p key={label}><span className="font-semibold">{label}:</span> {value || 'N/A'}</p>
                            ))}
                          </div>
                          <div className="mt-4 text-sm text-gray-500">
                            <p>Created: {new Date(model.created_at).toLocaleString()}</p>
                            <p>Updated: {new Date(model.updated_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredModels.length === 0 && (
          <div className="bg-white shadow-md rounded-lg p-6 text-center mt-6">
            <Typography variant="h6" className="text-gray-500">
              No models found matching the current filters.
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIModels;